// CRM Phase 3 — Subsidiary Org Hierarchy
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Building2, Trash2, Edit2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function buildTree(orgs) {
  const byId = Object.fromEntries(orgs.map(o => [o.id, { ...o, children: [] }]));
  const roots = [];
  for (const o of Object.values(byId)) {
    if (o.parent_org_id && byId[o.parent_org_id]) byId[o.parent_org_id].children.push(o);
    else roots.push(o);
  }
  return roots;
}

/** Flatten tree to depth-tagged array via iterative DFS (avoids self-recursion). */
function flattenTree(nodes, depth = 0, out = []) {
  const stack = [...nodes.map(n => ({ node: n, depth }))].reverse();
  while (stack.length) {
    const { node, depth: d } = stack.pop();
    out.push({ node, depth: d });
    for (let i = node.children.length - 1; i >= 0; i--) {
      stack.push({ node: node.children[i], depth: d + 1 });
    }
  }
  return out;
}

export default function CrmOrgs() {
  const [orgs, setOrgs] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", parent_org_id: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/crm/orgs");
    setOrgs(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const tree = buildTree(orgs);
  const flat = flattenTree(tree);

  const openNew = () => { setEditing(null); setForm({ name: "", parent_org_id: "" }); setDlg(true); };
  const openEdit = (o) => { setEditing(o); setForm({ name: o.name, parent_org_id: o.parent_org_id || "" }); setDlg(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = { name: form.name.trim(), parent_org_id: form.parent_org_id || null };
    try {
      if (editing) await api.put(`/crm/orgs/${editing.id}`, payload);
      else await api.post("/crm/orgs", payload);
      toast.success(editing ? "Updated" : "Org created");
      setDlg(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="crm-orgs-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl">Subsidiary Hierarchy</h2>
          <p className="text-sm text-muted-foreground mt-1">Model parent-child relationships across your customer organizations.</p>
        </div>
        <Button onClick={openNew} data-testid="org-new-btn"><Plus className="w-4 h-4 mr-1" /> New org</Button>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6">
        {flat.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">No organizations yet.</div>
        ) : (
          flat.map(({ node, depth }) => (
            <div
              key={node.id}
              className="flex items-center gap-2 py-2 pr-2 rounded-lg hover:bg-muted/50 group"
              style={{ paddingLeft: `${depth * 20}px` }}
              data-testid={`org-node-${node.id}`}
            >
              <Building2 className="w-4 h-4 text-primary shrink-0" />
              <span className="font-medium">{node.name}</span>
              {node.children.length > 0 && (
                <span className="text-[10px] overline text-muted-foreground">{node.children.length} sub</span>
              )}
              <Button size="sm" variant="ghost" onClick={() => openEdit(node)} className="ml-auto opacity-0 group-hover:opacity-100" data-testid={`org-edit-${node.id}`}>
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit organization" : "New organization"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="org-name-input" /></div>
            <div>
              <Label>Parent org (optional)</Label>
              <Select value={form.parent_org_id || "none"} onValueChange={v => setForm({ ...form, parent_org_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="org-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None (top level) —</SelectItem>
                  {orgs.filter(o => !editing || o.id !== editing.id).map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(false)}>Cancel</Button>
            <Button onClick={save} data-testid="org-save-btn">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
